import { ErrorLogComponent } from '@cian/error-log-component';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { NewbuildingPurchaseGiftExperiment } from './NewbuildingPurchaseGiftExperiment';
import { NewbuildingPurchaseGiftModalLoadable } from './NewbuildingPurchaseGiftModalLoadable';
import {
  INewbuildingPurchaseGiftPromoContext,
  NewbuildingPurchaseGiftPromoContext,
  TNewbuildingPurchaseGiftPromoSource,
} from './NewbuildingPurchaseGiftPromoContext';
import {
  trackCopyEmail,
  trackEntryPointClick,
  trackModalClose,
  trackModalConfirm,
  trackModalShow,
  trackStepView,
  trackTermsClick,
} from './tracking';
import {
  NEWBUILDING_PURCHASE_GIFT_EMAIL_KEY,
  NEWBUILDING_PURCHASE_GIFT_ENABLED_KEY,
  NEWBUILDING_PURCHASE_GIFT_REGIONS,
  NEWBUILDING_PURCHASE_GIFT_TERMS_URL_KEY,
} from '../../constants/newbuildingPurchaseGiftPromo';
import { getRegionId } from '../../selectors/newbuilding/getRegionId';
import { useApplicationContext } from '../../utils/withApplicationContext';

interface INewbuildingPurchaseGiftPromoConfig {
  email: string;
  termsUrl: string;
}

interface IGateProps extends React.PropsWithChildren {
  config: INewbuildingPurchaseGiftPromoConfig;
}

const PromoModalHost: React.FC<IGateProps> = ({ config, children }) => {
  const { logger } = useApplicationContext();
  const [open, setOpen] = React.useState(false);
  /* Чанк модалки грузится только после первого открытия, дальше модалка остаётся смонтированной */
  const [isModalMounted, setIsModalMounted] = React.useState(false);
  const sourceRef = React.useRef<TNewbuildingPurchaseGiftPromoSource>('banner');

  const openModal = React.useCallback((source: TNewbuildingPurchaseGiftPromoSource) => {
    trackEntryPointClick(source);
    sourceRef.current = source;
    setIsModalMounted(true);
    setOpen(true);
  }, []);

  const contextValue = React.useMemo<INewbuildingPurchaseGiftPromoContext>(
    () => ({ isAvailable: true, openModal }),
    [openModal],
  );

  const handleShow = React.useCallback(() => trackModalShow(sourceRef.current), []);

  return (
    <NewbuildingPurchaseGiftPromoContext.Provider value={contextValue}>
      {children}
      {isModalMounted && (
        <ErrorLogComponent logger={logger}>
          <NewbuildingPurchaseGiftModalLoadable
            open={open}
            onOpenChange={setOpen}
            config={config}
            onShow={handleShow}
            onClose={trackModalClose}
            onConfirmClick={trackModalConfirm}
            onCopyEmailClick={trackCopyEmail}
            onTermsClick={trackTermsClick}
            onStepView={trackStepView}
          />
        </ErrorLogComponent>
      )}
    </NewbuildingPurchaseGiftPromoContext.Provider>
  );
};

const ExperimentGate: React.FC<IGateProps> = ({ config, children }) => {
  const group = NewbuildingPurchaseGiftExperiment.use();

  if (group !== 'B') {
    return children;
  }

  return <PromoModalHost config={config}>{children}</PromoModalHost>;
};

/**
 * Провайдер акции «30 000 ₽ за покупку квартиры» для карточки ЖК:
 * считает доступность точек входа и держит общую модалку для баннера и лейбла в плавающей шапке.
 * Эксперимент читается только когда тогл включён, конфиг заполнен и регион ЖК подходит,
 * чтобы exposure уходил лишь там, где точка входа могла показаться.
 */
export const NewbuildingPurchaseGiftPromoProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { config } = useApplicationContext();
  const regionId = useSelector(getRegionId);

  const isEnabled = Boolean(config.get<boolean>(NEWBUILDING_PURCHASE_GIFT_ENABLED_KEY));
  const email = config.get<string>(NEWBUILDING_PURCHASE_GIFT_EMAIL_KEY) || '';
  const termsUrl = config.get<string>(NEWBUILDING_PURCHASE_GIFT_TERMS_URL_KEY) || '';
  const isInRegion = regionId !== null && NEWBUILDING_PURCHASE_GIFT_REGIONS.includes(regionId);
  const promoConfig = React.useMemo<INewbuildingPurchaseGiftPromoConfig>(
    () => ({ email, termsUrl }),
    [email, termsUrl],
  );

  if (!isEnabled || !email || !termsUrl || !isInRegion) {
    return children;
  }

  return <ExperimentGate config={promoConfig}>{children}</ExperimentGate>;
};
